import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Archive, RotateCcw, Briefcase, Users, UserCog, Calendar, DollarSign, Phone, Mail, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` });

const STATUS_MAP = {
  beklemede: 'Beklemede', randevu_planlandi: 'Randevu Planlandi', arac_kabul: 'Arac Kabul',
  islemde: 'Islemde', kalite_kontrol: 'Kalite Kontrol', teslime_hazir: 'Teslime Hazir',
  teslim_edildi: 'Teslim Edildi', pending: 'Beklemede', in_progress: 'Islemde',
  ready: 'Teslime Hazir', completed: 'Teslim Edildi',
};

const ROLE_LABELS = { admin: 'Admin', manager: 'Yonetici', sales: 'Satis', technician: 'Uygulamaci' };

const ArchiveTab = () => {
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [archivedStaff, setArchivedStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState('');

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const h = getHeaders();
      const [jRes, cRes, sRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/jobs/archived/list`, { headers: h }),
        fetch(`${BACKEND_URL}/api/customers/archived/list`, { headers: h }),
        fetch(`${BACKEND_URL}/api/staff/archived/list`, { headers: h }),
      ]);
      if (jRes.ok) { const d = await jRes.json(); setArchivedJobs(d.jobs || []); }
      if (cRes.ok) { const d = await cRes.json(); setArchivedCustomers(d.customers || []); }
      if (sRes.ok) { const d = await sRes.json(); setArchivedStaff(d.staff || []); }
    } catch (e) {
      toast({ title: "Hata", description: "Arsiv yuklenemedi", variant: "destructive" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadArchived(); }, [loadArchived]);

  const handleRestore = async (type, id, name) => {
    if (!window.confirm(`"${name}" geri yuklenecek. Emin misiniz?`)) return;
    try {
      let url = '';
      if (type === 'job') url = `${BACKEND_URL}/api/jobs/${id}/unarchive`;
      else if (type === 'customer') url = `${BACKEND_URL}/api/customers/${id}/restore`;
      else if (type === 'staff') url = `${BACKEND_URL}/api/staff/${id}/restore`;

      const res = await fetch(url, { method: 'POST', headers: getHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Geri yukleme basarisiz'); }
      toast({ title: "Geri Yuklendi", description: `"${name}" basariyla geri yuklendi.` });
      setDetailItem(null);
      loadArchived();
    } catch (e) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
  };

  const handlePermanentDelete = async (type, id, name) => {
    const msg = `DIKKAT: "${name}" KALICI olarak silinecek!\n\nBu islem GERi ALINAMAZ. Tum iliskili veriler kaybolabilir.\n\nDevam etmek istiyor musunuz?`;
    if (!window.confirm(msg)) return;
    const msg2 = `SON UYARI: "${name}" kalici olarak silinecek. Bu islem geri alinamaz. EVET yazarak onaylayin.`;
    const confirmed = window.prompt(msg2);
    if (confirmed !== 'EVET') {
      toast({ title: "Iptal Edildi", description: "Kalici silme iptal edildi." });
      return;
    }
    try {
      let url = '';
      if (type === 'job') url = `${BACKEND_URL}/api/jobs/${id}/permanent`;
      else if (type === 'customer') url = `${BACKEND_URL}/api/customers/${id}/permanent`;
      else if (type === 'staff') url = `${BACKEND_URL}/api/staff/${id}/permanent`;

      const res = await fetch(url, { method: 'DELETE', headers: getHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Kalici silme basarisiz'); }
      toast({ title: "Kalici Silindi", description: `"${name}" kalici olarak silindi.` });
      setDetailItem(null);
      loadArchived();
    } catch (e) {
      toast({ title: "Hata", description: e.message, variant: "destructive" });
    }
  };

  const openDetail = (item, type) => {
    setDetailItem(item);
    setDetailType(type);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('tr-TR') : '-';

  return (
    <div className="space-y-4" data-testid="archive-tab">
      <div className="flex items-center gap-2 mb-2">
        <Archive className="h-5 w-5 text-yellow-500" />
        <h2 className="text-white font-semibold text-lg">Arsiv</h2>
        <Badge className="bg-gray-700 text-white text-xs">
          {archivedJobs.length + archivedCustomers.length + archivedStaff.length} kayit
        </Badge>
        <Button size="sm" variant="outline" onClick={loadArchived} disabled={loading}
          className="border-gray-600 text-gray-300 text-xs h-7 ml-auto" data-testid="refresh-archive-btn">
          <RotateCcw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Yenile
        </Button>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList className="bg-gray-800 border border-gray-700 mb-3 gap-1 p-1">
          <TabsTrigger value="jobs" className="data-[state=active]:bg-gray-700 text-gray-400 data-[state=active]:text-white text-xs gap-1 px-3 py-1.5">
            <Briefcase className="h-3 w-3" />Isler ({archivedJobs.length})
          </TabsTrigger>
          <TabsTrigger value="customers" className="data-[state=active]:bg-gray-700 text-gray-400 data-[state=active]:text-white text-xs gap-1 px-3 py-1.5">
            <Users className="h-3 w-3" />Musteriler ({archivedCustomers.length})
          </TabsTrigger>
          <TabsTrigger value="staff" className="data-[state=active]:bg-gray-700 text-gray-400 data-[state=active]:text-white text-xs gap-1 px-3 py-1.5">
            <UserCog className="h-3 w-3" />Personel ({archivedStaff.length})
          </TabsTrigger>
        </TabsList>

        {/* Archived Jobs */}
        <TabsContent value="jobs" data-testid="archive-jobs">
          {archivedJobs.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Arsivlenmis is yok</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {archivedJobs.map(j => (
                <Card key={j.id} className="bg-gray-800/50 border-gray-700/40 cursor-pointer hover:border-brand/40 transition-colors"
                  onClick={() => openDetail(j, 'job')} data-testid={`archived-job-${j.id}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm">{j.customerName}</p>
                          <span className="text-brand text-xs">{j.carPlate}</span>
                          <Badge className="bg-yellow-700 text-white text-[10px]">Arsivlendi</Badge>
                          <Badge className="bg-gray-600 text-white text-[10px]">{STATUS_MAP[j.status] || j.status}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                          <span>Hizmetler: {(j.services || []).join(', ')}</span>
                          {j.archivedAt && <span>Arsivlenme: {formatDate(j.archivedAt)}</span>}
                          {j.archivedBy && <span>Arsivleyen: {j.archivedBy}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-white font-bold text-sm">{(j.currentPrice ?? j.totalPrice)?.toLocaleString()} TL</span>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleRestore('job', j.id, j.customerName); }}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`restore-job-${j.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" />Geri Yukle
                        </Button>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handlePermanentDelete('job', j.id, j.customerName); }}
                          className="bg-red-700 hover:bg-red-800 text-white text-xs h-7" data-testid={`perm-delete-job-${j.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Archived Customers */}
        <TabsContent value="customers" data-testid="archive-customers">
          {archivedCustomers.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Arsivlenmis musteri yok</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {archivedCustomers.map(c => (
                <Card key={c.id} className="bg-gray-800/50 border-gray-700/40 cursor-pointer hover:border-brand/40 transition-colors"
                  onClick={() => openDetail(c, 'customer')} data-testid={`archived-customer-${c.id}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm">{c.name}</p>
                          <Badge className="bg-yellow-700 text-white text-[10px]">Arsivlendi</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                          {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                          {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                          {c.carPlate && <span className="text-brand">{c.carPlate}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleRestore('customer', c.id, c.name); }}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`restore-customer-${c.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" />Geri Yukle
                        </Button>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handlePermanentDelete('customer', c.id, c.name); }}
                          className="bg-red-700 hover:bg-red-800 text-white text-xs h-7" data-testid={`perm-delete-customer-${c.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Archived Staff */}
        <TabsContent value="staff" data-testid="archive-staff">
          {archivedStaff.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">Arsivlenmis personel yok</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {archivedStaff.map(s => (
                <Card key={s.id} className="bg-gray-800/50 border-gray-700/40 cursor-pointer hover:border-brand/40 transition-colors"
                  onClick={() => openDetail(s, 'staff')} data-testid={`archived-staff-${s.id}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm">{s.name}</p>
                          <Badge className="bg-yellow-700 text-white text-[10px]">Arsivlendi</Badge>
                          <Badge className="bg-indigo-600 text-white text-[10px]">{ROLE_LABELS[s.role] || s.role}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                          {s.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>}
                          {s.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{s.phone}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleRestore('staff', s.id, s.name); }}
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`restore-staff-${s.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" />Geri Yukle
                        </Button>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handlePermanentDelete('staff', s.id, s.name); }}
                          className="bg-red-700 hover:bg-red-800 text-white text-xs h-7" data-testid={`perm-delete-staff-${s.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={!!detailItem} onOpenChange={() => setDetailItem(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Archive className="h-5 w-5 text-yellow-500" />
              Arsiv Detayi
            </DialogTitle>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-3" data-testid="archive-detail-modal">
              {detailType === 'job' && (
                <>
                  <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between items-center">
                      <h3 className="text-white font-semibold">{detailItem.customerName}</h3>
                      <Badge className="bg-gray-600 text-white text-xs">{STATUS_MAP[detailItem.status] || detailItem.status}</Badge>
                    </div>
                    <p className="text-brand text-sm">{detailItem.carPlate}</p>
                    <div className="flex flex-wrap gap-1">
                      {(detailItem.services || []).map((s, i) => (
                        <Badge key={`arc-svc-${s}-${i}`} variant="outline" className="border-gray-600 text-gray-300 text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="bg-gray-800 rounded-lg p-2">
                      <p className="text-gray-400 text-[10px]">Tutar</p>
                      <p className="text-white font-bold">{(detailItem.currentPrice ?? detailItem.totalPrice)?.toLocaleString()} TL</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-2">
                      <p className="text-gray-400 text-[10px]">Odenen</p>
                      <p className="text-green-400 font-bold">{detailItem.paidAmount?.toLocaleString()} TL</p>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-2">
                      <p className="text-gray-400 text-[10px]">Kalan</p>
                      <p className="text-red-400 font-bold">{detailItem.remainingBalance?.toLocaleString()} TL</p>
                    </div>
                  </div>
                  {detailItem.notes && (
                    <div className="bg-gray-800/50 rounded p-2">
                      <p className="text-gray-400 text-[10px]">Notlar</p>
                      <p className="text-white text-sm">{detailItem.notes}</p>
                    </div>
                  )}
                  {detailItem.archivedAt && (
                    <div className="bg-yellow-900/20 border border-yellow-700/30 rounded p-2 text-xs">
                      <p className="text-yellow-400">Arsivlenme: {formatDate(detailItem.archivedAt)}</p>
                      {detailItem.archivedBy && <p className="text-yellow-500">Arsivleyen: {detailItem.archivedBy}</p>}
                    </div>
                  )}
                  {detailItem.payments?.length > 0 && (
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Odeme Gecmisi</p>
                      {detailItem.payments.map((p, i) => (
                        <div key={p.id || `arc-pay-${p.date}-${i}`} className="flex justify-between text-xs py-0.5 border-b border-gray-700/30">
                          <span className="text-gray-300">{formatDate(p.date)} - {p.method}</span>
                          <span className="text-green-400">{p.amount?.toLocaleString()} TL</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {detailType === 'customer' && (
                <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                  <h3 className="text-white font-semibold text-lg">{detailItem.name}</h3>
                  {detailItem.email && <p className="text-gray-400 text-sm flex items-center gap-1"><Mail className="h-3 w-3" />{detailItem.email}</p>}
                  {detailItem.phone && <p className="text-gray-400 text-sm flex items-center gap-1"><Phone className="h-3 w-3" />{detailItem.phone}</p>}
                  {detailItem.carPlate && <p className="text-brand text-sm">{detailItem.carPlate}</p>}
                  {detailItem.carDetails && <p className="text-gray-500 text-xs">{detailItem.carDetails}</p>}
                  {detailItem.registeredDate && <p className="text-gray-500 text-xs">Kayit: {formatDate(detailItem.registeredDate)}</p>}
                  {detailItem.portalStatus && <p className="text-gray-500 text-xs">Portal: {detailItem.portalStatus}</p>}
                  {detailItem.bonusBalance > 0 && <p className="text-green-400 text-xs">Bonus: {detailItem.bonusBalance} TL</p>}
                </div>
              )}

              {detailType === 'staff' && (
                <div className="bg-gray-800 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold text-lg">{detailItem.name}</h3>
                    <Badge className="bg-indigo-600 text-white text-xs">{ROLE_LABELS[detailItem.role] || detailItem.role}</Badge>
                  </div>
                  {detailItem.email && <p className="text-gray-400 text-sm flex items-center gap-1"><Mail className="h-3 w-3" />{detailItem.email}</p>}
                  {detailItem.phone && <p className="text-gray-400 text-sm flex items-center gap-1"><Phone className="h-3 w-3" />{detailItem.phone}</p>}
                  {detailItem.permissions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailItem.permissions.slice(0, 8).map(p => (
                        <Badge key={p} variant="outline" className="border-gray-600 text-gray-400 text-[10px]">{p}</Badge>
                      ))}
                      {detailItem.permissions.length > 8 && (
                        <Badge variant="outline" className="border-gray-600 text-gray-400 text-[10px]">+{detailItem.permissions.length - 8}</Badge>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={() => handleRestore(detailType, detailItem.id, detailItem.name || detailItem.customerName || '')}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="restore-from-detail-btn">
                  <RotateCcw className="h-4 w-4 mr-1" />Geri Yukle
                </Button>
                <Button onClick={() => handlePermanentDelete(detailType, detailItem.id, detailItem.name || detailItem.customerName || '')}
                  className="bg-red-700 hover:bg-red-800 text-white" data-testid="perm-delete-from-detail-btn">
                  <Trash2 className="h-4 w-4 mr-1" />Kalici Sil
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchiveTab;
